import moment from 'moment';
import * as uuid from 'uuid';

import type {
  ChatConversation,
  GeneralInformationAuthenticationToBook,
} from '../../../../../common';

import * as api from '../../../../../api';
import Reconciliation from '../GeneralUi/Reconciliation';
import { useContext } from 'react';
import { ConversationContext } from '../../../../../context/conversationContext';
import BaseMessage from '../Base';
import ViewOrPayBill from '../Intent/Payment';

export const SUPPORTED_INTENT_RECOMMENDATION: {
  [key in api.Assistant.Intent]?: api.Assistant.Recommendation[];
} = {
  [api.Assistant.Intent.PrescriptionRefill]: [
    api.Assistant.Recommendation.Authentication,
    api.Assistant.Recommendation.Scheduling,
  ],
  [api.Assistant.Intent.Payment]: [api.Assistant.Recommendation.Authentication],
  [api.Assistant.Intent.ProviderSearch]: [
    api.Assistant.Recommendation.Authentication,
  ],
  [api.Assistant.Intent.ProviderScheduling]: [
    api.Assistant.Recommendation.Authentication,
  ],
  [api.Assistant.Intent.GeneralInformation]: [
    api.Assistant.Recommendation.Authentication,
  ],
};

interface Props {
  recommendation: api.Assistant.Recommendation;
  message: ChatConversation;
  showTextAnimation: boolean;
}

const isMessageAuthToBook = (
  message: ChatConversation,
): message is GeneralInformationAuthenticationToBook => {
  if (!('data' in message)) return false;
  return !!message.data.ehrTimeSlotToBook;
};

const Recommendation: React.FC<Props> = ({
  recommendation,
  message,
  showTextAnimation,
}) => {
  const conversationContext = useContext(ConversationContext);
  const handleReconciliationSuccess = (
    loginInfo: Pick<
      api.AuthPatient.AuthPatientBody,
      'firstName' | 'lastName' | 'dateOfBirth'
    >,
  ) => {
    if (!conversationContext) return;
    conversationContext.disableMessages();
    if (isMessageAuthToBook(message)) {
      const { provider, ehrTimeSlotToBook } = message.data;
      conversationContext.addNewMessage({
        chat_message_id: uuid.v4(),
        source_documents: [],
        response_entities: {},
        user_entities: {},
        done: true,
        answer: 'Your appointment was booked.',
        intent: api.Assistant.Intent.GeneralInformation,
        intentComponent:
          api.Assistant.IntentComponent.GeneralInformationAppointmentConfirmed,
        data: {
          appointment: {
            providerFirstName: provider.firstName,
            providerLastName: provider.lastName,
            startTime: ehrTimeSlotToBook.startTime,
            providerPrefix: provider.prefix,
            providerSuffix: provider.suffix,
            previsitImageUrl: provider.previsitImageUrl,
            practiceName: provider.practices[0]?.name ?? 'PCMA',
            phoneNumber:
              provider.phoneNumber ??
              provider.practices[0]?.phoneNumber ??
              '3211234895',
            timezone: provider.practices[0]?.timezone ?? 'America/Los_Angeles',
            ehrTimeSlotBookingId: '1234',
            cancellationSuccess: false,
            address: provider.practices[0]?.address ?? '',
            state: provider.practices[0]?.state ?? '',
            city: provider.practices[0]?.city ?? '',
            zipCode: provider.practices[0]?.zip ?? '',
          },
        },
      });
    } else {
      conversationContext.addNewMessage({
        question: `${loginInfo.firstName} ${loginInfo.lastName}, ${moment(
          loginInfo.dateOfBirth,
        ).format('MMM Do, YYYY')}`,
        chat_message_id: uuid.v4(),
        done: true,
        disabled: true,
        hidden: true,
      });
    }
  };

  const commonProps = { message, showTextAnimation };

  switch (recommendation) {
    case api.Assistant.Recommendation.Authentication:
      return (
        <Reconciliation
          onReconciliationSuccess={handleReconciliationSuccess}
          showMessageAnswer={true}
          {...commonProps}
        />
      );
    case api.Assistant.Recommendation.Scheduling:
      return (
        <BaseMessage
          message={{
            ...message,
            recommendedProviders: [
              {
                id: uuid.v4(),
                firstName: 'Adam',
                lastName: 'Walker',
                prefix: null,
                suffix: 'MD',
                previsitImageUrl:
                  'https://notable-providers.s3.us-east-2.amazonaws.com/Adam-Walker.png',
                sex: api.User.Sex.Female,
                virtualVisitUrl: null,
                phoneNumber: '4082033000',
                profile: {
                  specialties: [api.Specialty.FamilyMedicine],
                  spokenLanguages: [
                    api.Language.Language.En,
                    api.Language.Language.Es,
                  ],
                },
                preference: { acceptsNewPatients: true },
                practices: [
                  {
                    address: '751 S Bascom Ave',
                    city: 'San Jose',
                    state: 'CA',
                    zip: '95128',
                    name: 'Pacific Coast Medical Associates',
                    timezone: 'America/Los_Angeles',
                    phoneNumber: '4082033000',
                  },
                ],
              },
            ],
          }}
          showTextAnimation={showTextAnimation}
        />
      );
    case api.Assistant.Recommendation.ViewOrPayBill:
      return <ViewOrPayBill {...commonProps} />;
  }

  return <></>;
};

export default Recommendation;
